import get from 'lodash/get';
import SectionIds from '../../tracking/SectionIds';
import SectionTypes from '../../tracking/SectionTypes';
import { replaceUrlParameter } from '../strings';
import { REDIRECT_URL_PARAMETERS } from '../../Serp/constants';
import applyPremiumAdsShowingRules from './applyPremiumAdsShowingRules';

export const decorateAdWithProperties = (ad, properties, index) => {
  let decoratedAd = {
    ...ad,
    ...properties,
    position: index + 1,
  };

  // Remove unused shareUrls
  if (get(decoratedAd, ['source', 'ad', 'shareUrls'])) {
    const {
      source: { ad: sourceAd },
    } = decoratedAd;
    const { shareUrls, ...sourceAdFiltered } = sourceAd;
    decoratedAd = { ...decoratedAd, source: { ...decoratedAd.source, ad: sourceAdFiltered } };
  }

  return decoratedAd;
};

export const decorateAdsWithProperties = (ads, properties) =>
  ads.map((ad, index) => decorateAdWithProperties(ad, properties, index));

const normalizeSearchAds = apiResponse => {
  let { organicAds, premiumAds, similarContentAds } = apiResponse.serp;
  const totalOrganicResults = organicAds.length || 0;

  let ads = [];
  if (totalOrganicResults > 0) {
    organicAds = decorateAdsWithProperties(organicAds, {
      section_id: SectionIds.SECTION_SEARCH_RESULTS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      description: undefined,
      descriptionOriginal: undefined,
    });

    ads = organicAds;
  }

  if (premiumAds.length > 0) {
    const {
      meta: {
        pagination: { page, maxPage },
      },
    } = apiResponse;

    premiumAds = applyPremiumAdsShowingRules(premiumAds, totalOrganicResults, page, maxPage);
    premiumAds.top = decorateAdsWithProperties(premiumAds.top, {
      section_id: SectionIds.SECTION_SEARCH_BANNER_TOP,
      section_type_id: SectionTypes.SECTION_TYPE_PREMIUM,
      description: undefined,
      descriptionOriginal: undefined,
    });
    premiumAds.bottom = decorateAdsWithProperties(premiumAds.bottom, {
      section_id: SectionIds.SECTION_SEARCH_BANNER_BOTTOM,
      section_type_id: SectionTypes.SECTION_TYPE_PREMIUM,
      description: undefined,
      descriptionOriginal: undefined,
    });
    ads = premiumAds.top.concat(ads).concat(premiumAds.bottom);
  }

  if (similarContentAds && similarContentAds.length > 0) {
    similarContentAds = decorateAdsWithProperties(similarContentAds, {
      section_id: SectionIds.SECTION_SEARCH_FEWRESULTS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      isSimilarContentAd: true,
      description: undefined,
      descriptionOriginal: undefined,
    });

    ads = ads.concat(similarContentAds);
  }

  return { ...apiResponse, data: ads };
};

const normalizeRetentionAds = apiResponse => {
  let {
    organicAds,
    alternativeAds,
    meta: { fresh: freshAds },
  } = apiResponse.serp;
  let ads = [];

  if (organicAds.length > 0) {
    organicAds = decorateAdsWithProperties(organicAds, {
      section_id: SectionIds.SECTION_SEARCH_ONLY_NEW_ADS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      description: undefined,
      descriptionOriginal: undefined,
    });

    ads = organicAds;
  }

  if (freshAds.length > 0) {
    freshAds = decorateAdsWithProperties(freshAds, {
      section_id: SectionIds.SECTION_SEARCH_ONLY_NEW_ADS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      description: undefined,
      descriptionOriginal: undefined,
    });

    ads = freshAds.concat(organicAds);
  }

  if (Array.isArray(alternativeAds) && alternativeAds.length > 0) {
    alternativeAds = decorateAdsWithProperties(alternativeAds, {
      section_id: SectionIds.SECTION_SEARCH_ONLY_NEW_ADS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      isAlternativeAd: false,
      description: undefined,
      descriptionOriginal: undefined,
    });
    ads = ads.concat(alternativeAds);
  }

  return { ...apiResponse, data: ads };
};

const normalizeLandingAds = apiResponse => {
  let { organicAds, premiumAds, alternativeAds, similarContentAds } = apiResponse.serp;

  let ads = [];

  if (premiumAds.length > 0) {
    premiumAds = decorateAdsWithProperties(premiumAds, {
      section_id: SectionIds.SECTION_PAID_REGULARLANDING_ADS,
      section_type_id: SectionTypes.SECTION_TYPE_PREMIUM,
      description: undefined,
      descriptionOriginal: undefined,
    });
    ads = ads.concat(premiumAds);
  }

  if (organicAds.length > 0) {
    organicAds = decorateAdsWithProperties(organicAds, {
      section_id: SectionIds.SECTION_PAID_REGULARLANDING_ADS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      description: undefined,
      descriptionOriginal: undefined,
    });
    ads = ads.concat(organicAds);
  }

  if (Array.isArray(alternativeAds) && alternativeAds.length > 0) {
    alternativeAds = decorateAdsWithProperties(alternativeAds, {
      section_id: SectionIds.SECTION_PAID_REGULARLANDING_ADS_MORE,
      section_type_id: SectionTypes.SECTION_TYPE_PREMIUM,
      isAlternativeAd: true,
      description: undefined,
      descriptionOriginal: undefined,
    });
    ads = ads.concat(alternativeAds);
  }

  if (similarContentAds && similarContentAds.length > 0) {
    similarContentAds = decorateAdsWithProperties(similarContentAds, {
      section_id: SectionIds.SECTION_PAID_SEARCH_FEWRESULTS,
      section_type_id: SectionTypes.SECTION_TYPE_ORGANIC,
      isSimilarContentAd: true,
      description: undefined,
      descriptionOriginal: undefined,
    });

    ads = ads.concat(similarContentAds);
  }

  return { ...apiResponse, data: ads };
};

const normalizeRelatedAds = (apiResponse, isFromRelatedModal) =>
  apiResponse.map((ad, index) =>
    decorateAdWithProperties(
      ad,
      {
        section_id: isFromRelatedModal
          ? SectionIds.SECTION_SEARCH_DUPLICATE
          : SectionIds.SECTION_ADPAGE_SIMILAR,
        section_type_id: ad.premium
          ? SectionTypes.SECTION_TYPE_PREMIUM
          : SectionTypes.SECTION_TYPE_ORGANIC,
        position: index + 1,
        redirectUrl: isFromRelatedModal
          ? replaceUrlParameter(
              ad.redirectUrl,
              REDIRECT_URL_PARAMETERS.section,
              SectionIds.SECTION_SEARCH_DUPLICATE
            )
          : ad.redirectUrl,
      },
      index
    )
  );

const normalizeLandingRelatedAds = (apiResponse, isFromRelatedModal) =>
  apiResponse.map((ad, index) =>
    decorateAdWithProperties(
      ad,
      {
        section_id: isFromRelatedModal
          ? SectionIds.SECTION_PAID_SEARCH_DUPLICATE
          : SectionIds.SECTION_ADPAGE_SIMILAR,
        section_type_id: ad.premium
          ? SectionTypes.SECTION_TYPE_PREMIUM
          : SectionTypes.SECTION_TYPE_ORGANIC,
        position: index + 1,
        redirectUrl: isFromRelatedModal
          ? replaceUrlParameter(
              ad.redirectUrl,
              REDIRECT_URL_PARAMETERS.section,
              SectionIds.SECTION_PAID_SEARCH_DUPLICATE
            )
          : ad.redirectUrl,
      },
      index
    )
  );

export default {
  normalizeSearchAds,
  normalizeRetentionAds,
  normalizeLandingAds,
  normalizeRelatedAds,
  normalizeLandingRelatedAds,
};



// WEBPACK FOOTER //
// ./app/utils/adNormalizers/normalizer.js